import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

// The navy report canvas colour — used to bleed each page edge-to-edge
// so the exported PDF has no white margins on the sides.
const CANVAS_BG = '#003B64'

// Ordered list of the report's top-level sections. Capturing per-section
// gives clean page breaks and keeps each page sized to fit its content.
const SECTION_IDS = [
  'overview',
  'performance',
  'analysis',
  'statistics',
  'recommendations',
]

/** Wait for every <img> inside an element to finish loading/decoding. */
async function waitForImages(el: HTMLElement): Promise<void> {
  const imgs = Array.from(el.querySelectorAll('img'))
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve()
      return new Promise<void>((resolve) => {
        const done = () => resolve()
        img.addEventListener('load', done, { once: true })
        img.addEventListener('error', done, { once: true })
      })
    }),
  )
}

/**
 * Renders the live report to a multi-page, full-bleed PDF.
 * Each section becomes its own page, sized exactly to the captured
 * image so there are no white borders and nothing is clipped.
 */
export async function exportReportToPdf(
  fileName = 'Lagardere-Attention-Report.pdf',
): Promise<void> {
  // Mark the document as exporting so print-only CSS can hide interactive
  // chrome, and notify components (e.g. the asset carousel) to switch into
  // a static, fully-expanded layout for the capture.
  document.body.classList.add('is-exporting')
  window.dispatchEvent(new CustomEvent('report:export-start'))

  // Give React a couple of frames to render the export layout.
  await new Promise((r) => requestAnimationFrame(() => r(null)))
  await new Promise((r) => requestAnimationFrame(() => r(null)))
  await new Promise((r) => setTimeout(r, 150))

  const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(
    (el): el is HTMLElement => Boolean(el),
  )

  if (sections.length === 0) {
    document.body.classList.remove('is-exporting')
    window.dispatchEvent(new CustomEvent('report:export-end'))
    throw new Error('No report sections found to export.')
  }

  try {
    let pdf: jsPDF | null = null

    for (const section of sections) {
      // Ensure all imagery (heatmaps, thumbnails) is loaded before capture.
      await waitForImages(section)

      const canvas = await html2canvas(section, {
        scale: 2,
        backgroundColor: CANVAS_BG,
        useCORS: true,
        logging: false,
        windowWidth: section.scrollWidth,
      })

      const imgData = canvas.toDataURL('image/png')
      // Use px units so the page matches the captured image 1:1 → no margins.
      const pageWidth = canvas.width
      const pageHeight = canvas.height
      const orientation = pageWidth >= pageHeight ? 'landscape' : 'portrait'

      if (!pdf) {
        pdf = new jsPDF({
          orientation,
          unit: 'px',
          format: [pageWidth, pageHeight],
          compress: true,
        })
      } else {
        pdf.addPage([pageWidth, pageHeight], orientation)
      }

      pdf.addImage(
        imgData,
        'PNG',
        0,
        0,
        pageWidth,
        pageHeight,
        undefined,
        'FAST',
      )
    }

    pdf?.save(fileName)
  } finally {
    document.body.classList.remove('is-exporting')
    window.dispatchEvent(new CustomEvent('report:export-end'))
  }
}
